/**
 * AKILA Rx Lens Flow — Alpine.js Component
 *
 * Step-based renderer that walks through a flow config array.
 * Supports: single_select, multi_select, rx_form, color_select (future).
 * Cart integration via Shopify /cart/add.js
 */

document.addEventListener('alpine:init', () => {

  Alpine.data('rxFlow', () => ({
    // --- State ---
    open: false,
    config: null,
    flow: null,
    steps: [],
    currentStepIndex: 0,
    selections: {},
    submitting: false,
    error: null,

    // Rx form fields
    rx: {
      od_sph: '',
      od_cyl: '',
      od_axis: '',
      os_sph: '',
      os_cyl: '',
      os_axis: '',
      pd_type: 'single',
      pd_single: '',
      pd_right: '',
      pd_left: '',
      rx_file: null,
      rx_image_url: '',
      rx_notes: '',
      add_blue_light: false
    },

    // Upload state
    uploading: false,
    uploadProgress: 0,  // 0-100
    uploadError: null,

    // --- Lifecycle ---
    init() {
      const el = document.getElementById('rx-flow-data');
      if (!el) return;
      this.config = JSON.parse(el.textContent);
      this.flow = this.config.flows[this.config.flowType];
      this.steps = this.flow.steps;

      // Load product data from ProductJSON
      const productJsonEl = document.getElementById('ProductJSON');
      if (productJsonEl) {
        const productData = JSON.parse(productJsonEl.textContent);
        this._variants = productData.variants || [];
        this._images = productData.images || [];
      } else {
        this._variants = [];
        this._images = [];
      }

      // Hide/show the rx button when variant availability changes
      const wrapper = document.querySelector('.js-rx-flow-wrapper');
      if (wrapper) {
        const sProduct = wrapper.closest('s-product');
        if (sProduct) {
          sProduct.addEventListener('variant:change', (e) => {
            const variant = e.detail?.variant;
            if (variant) {
              wrapper.classList.toggle('hidden', !variant.available);
            }
          });
        }
      }
    },

    // --- Computed ---
    get currentStep() {
      return this.steps[this.currentStepIndex] || null;
    },

    get isFirstStep() {
      return this.currentStepIndex === 0;
    },

    get isLastStep() {
      return this.currentStepIndex === this.steps.length - 1;
    },

    get canAdvance() {
      const step = this.currentStep;
      if (!step) return false;

      if (step.type === 'single_select') {
        return !!this.selections[step.id];
      }
      if (step.type === 'multi_select') {
        return true; // add-ons are optional
      }
      if (step.type === 'rx_form') {
        return this.isRxValid;
      }
      if (step.type === 'review') {
        return true; // review is always valid — user can submit or go back
      }
      return true;
    },

    get isRxValid() {
      const r = this.rx;
      // SPH required for both eyes
      if (!r.od_sph || !r.os_sph) return false;
      // CYL optional, but if set, AXIS required
      if (r.od_cyl && r.od_cyl !== '0.00' && !r.od_axis) return false;
      if (r.os_cyl && r.os_cyl !== '0.00' && !r.os_axis) return false;
      // PD required
      if (r.pd_type === 'single' && !r.pd_single) return false;
      if (r.pd_type === 'dual' && (!r.pd_right || !r.pd_left)) return false;
      // Rx image required
      if (!r.rx_file) return false;
      return true;
    },

    get selectedLens() {
      const lensStep = this.steps.find(s => s.id === 'lens_type');
      if (!lensStep || !this.selections.lens_type) return null;
      return lensStep.options.find(o => o.value === this.selections.lens_type);
    },

    get subtotal() {
      let total = this.config.product.price;
      const lens = this.selectedLens;
      if (lens) total += lens.price;

      // Add any multi_select add-ons
      this.steps.forEach(step => {
        if (step.type === 'multi_select' && this.selections[step.id]) {
          this.selections[step.id].forEach(val => {
            const opt = step.options.find(o => o.value === val);
            if (opt) {
              if (opt.price) total += opt.price;
              if (opt.priceModifier) total += opt.priceModifier;
            }
          });
        }
      });

      return total;
    },

    get subtotalFormatted() {
      return this.formatMoney(this.subtotal);
    },

    get framePriceFormatted() {
      return this.formatMoney(this.config.product.price);
    },

    get lensPriceFormatted() {
      const lens = this.selectedLens;
      return lens ? this.formatMoney(lens.price) : '';
    },

    get rxSummary() {
      const r = this.rx;
      const od = [r.od_sph];
      if (r.od_cyl && r.od_cyl !== '0.00') od.push(r.od_cyl, 'x' + r.od_axis);
      const os = [r.os_sph];
      if (r.os_cyl && r.os_cyl !== '0.00') os.push(r.os_cyl, 'x' + r.os_axis);

      let pd;
      if (r.pd_type === 'single') {
        pd = r.pd_single;
      } else {
        pd = 'R' + r.pd_right + ' / L' + r.pd_left;
      }

      return {
        od: od.join(' / '),
        os: os.join(' / '),
        pd: pd
      };
    },

    // --- Methods ---
    openModal() {
      // Read the currently selected variant from the master <select>
      const masterSelect = document.querySelector('[id^="MasterSelect-"]');
      if (masterSelect) {
        const variantId = parseInt(masterSelect.value, 10);
        if (variantId && this._variants) {
          const variant = this._variants.find(v => v.id === variantId);
          if (variant) {
            this.config.product.variantId = variant.id;
            this.config.product.price = variant.price;
            this.config.product.sku = variant.sku || '';
            this.config.product.variantTitle = variant.title || '';
          }
        }
      }

      // Find the variant's image — fallback chain
      if (masterSelect) {
        const vid = parseInt(masterSelect.value, 10);
        const variant = this._variants ? this._variants.find(v => v.id === vid) : null;
        let img = null;

        // 1. Try Shopify's variant_ids association on images
        img = this._images.find(i => i.variant_ids && i.variant_ids.includes(vid));

        // 2. Try the variant's own featured_image
        if (!img && variant && variant.featured_image) {
          this.config.product.image = variant.featured_image.src;
        }

        // 3. Try matching image alt text to variant title
        if (!img && !variant?.featured_image && variant) {
          img = this._images.find(i => i.alt && i.alt === variant.title);
        }

        if (img) {
          this.config.product.image = img.src;
        }
      }

      this.open = true;
      this.currentStepIndex = 0;
      this.selections = {};
      this.rx = {
        od_sph: '', od_cyl: '', od_axis: '',
        os_sph: '', os_cyl: '', os_axis: '',
        pd_type: 'single', pd_single: '', pd_right: '', pd_left: '',
        rx_file: null, rx_image_url: '',
        rx_notes: '',
        add_blue_light: false
      };
      this.error = null;
      this.submitting = false;
      this.uploading = false;
      this.uploadProgress = 0;
      this.uploadError = null;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('rx-modal-open');
      // Move focus into the dialog (WCAG 2.4.3); remember the trigger so closeModal can hand focus back.
      window.__rxOpener = document.activeElement;
      this.focusStepTitle();
    },

    // Screen-reader users hear the new step announced because the title takes focus.
    focusStepTitle() {
      this.$nextTick(() => {
        const title = document.querySelector('.rx-modal .rx-step-title');
        if (title) title.focus();
      });
    },

    closeModal() {
      this.open = false;
      document.body.style.overflow = '';
      document.body.classList.remove('rx-modal-open');
      const opener = window.__rxOpener;
      if (opener && typeof opener.focus === 'function') opener.focus();
      window.__rxOpener = null;
    },

    selectOption(stepId, value) {
      this.selections[stepId] = value;
    },

    toggleMultiOption(stepId, value) {
      if (!this.selections[stepId]) {
        this.selections[stepId] = [];
      }
      const idx = this.selections[stepId].indexOf(value);
      if (idx > -1) {
        this.selections[stepId].splice(idx, 1);
      } else {
        this.selections[stepId].push(value);
      }
    },

    isSelected(stepId, value) {
      const sel = this.selections[stepId];
      if (Array.isArray(sel)) return sel.includes(value);
      return sel === value;
    },

    nextStep() {
      if (!this.canAdvance) return;
      if (this.currentStepIndex < this.steps.length - 1) {
        // Skip steps whose dependsOn conditions aren't met
        let next = this.currentStepIndex + 1;
        while (next < this.steps.length && !this.shouldShowStep(this.steps[next])) {
          next++;
        }
        if (next < this.steps.length) {
          this.currentStepIndex = next;
          this.focusStepTitle();
        }
      }
    },

    prevStep() {
      if (this.currentStepIndex > 0) {
        let prev = this.currentStepIndex - 1;
        while (prev >= 0 && !this.shouldShowStep(this.steps[prev])) {
          prev--;
        }
        if (prev >= 0) {
          this.currentStepIndex = prev;
          this.focusStepTitle();
        }
      }
    },

    shouldShowStep(step) {
      if (!step.dependsOn) return true;
      const depVal = this.selections[step.dependsOn.step];
      if (!depVal) return false;
      return step.dependsOn.values.includes(depVal);
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploadError = null;

      const MAX_SIZE = 10 * 1024 * 1024; // 10MB
      if (file.size > MAX_SIZE) {
        this.uploadError = 'File is too large (max 10MB). Please choose a smaller file.';
        event.target.value = '';
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.uploadError = 'Please upload an image (JPG, PNG, WebP, HEIC) or PDF.';
        event.target.value = '';
        return;
      }

      this.rx.rx_file = file;
    },

    async uploadRxImage() {
      if (!this.rx.rx_file) return '';

      const UPLOAD_ENDPOINT = 'https://akila-rx-upload.chris-caa.workers.dev';

      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadError = null;

      const formData = new FormData();
      formData.append('file', this.rx.rx_file);

      try {
        // Use XMLHttpRequest for upload progress tracking
        const data = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          let processingInterval = null;

          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              // Upload is 0-70%, processing/polling is 70-100%
              this.uploadProgress = Math.round((e.loaded / e.total) * 70);
            }
          });

          // Upload bytes finished — server is now processing (staged upload + polling).
          // Start animating 70→95% while we wait for the response.
          xhr.upload.addEventListener('loadend', () => {
            this.uploadProgress = 72;
            processingInterval = setInterval(() => {
              if (this.uploadProgress < 95) {
                this.uploadProgress += 1;
              }
            }, 400);
          });

          xhr.addEventListener('load', () => {
            clearInterval(processingInterval);

            try {
              const result = JSON.parse(xhr.responseText);

              if (xhr.status >= 400) {
                reject(new Error(result.error || 'Prescription upload failed'));
              } else if (!result.url) {
                reject(new Error('Prescription upload did not return a URL'));
              } else {
                this.uploadProgress = 100;
                resolve(result);
              }
            } catch (e) {
              reject(new Error('Prescription upload failed'));
            }
          });

          xhr.addEventListener('error', () => {
            clearInterval(processingInterval);
            reject(new Error('Network error — please check your connection'));
          });

          xhr.open('POST', UPLOAD_ENDPOINT);
          xhr.send(formData);
        });

        return data.url;
      } finally {
        this.uploading = false;
      }
    },

    async submit() {
      if (!this.canAdvance || this.submitting) return;
      this.submitting = true;
      this.error = null;

      try {
        // Upload Rx image if provided
        let rxImageUrl = '';
        if (this.rx.rx_file) {
          rxImageUrl = await this.uploadRxImage();
        }

        const bundleId = Date.now().toString();
        const lens = this.selectedLens;
        if (!lens) throw new Error('No lens selected');

        const items = [];

        // Frame
        items.push({
          id: this.config.product.variantId,
          quantity: 1,
          properties: {
            '_rx_bundle_id': bundleId,
            '_rx_bundle_role': 'frame'
          }
        });

        // Lens — build readable grouped properties for admin
        const r = this.rx;

        // Format OD line: SPH / CYL x AXIS (omit CYL/AXIS if none)
        let odStr = r.od_sph;
        if (r.od_cyl && r.od_cyl !== '0.00') {
          odStr += ' / ' + r.od_cyl + ' x ' + r.od_axis + '°';
        }

        let osStr = r.os_sph;
        if (r.os_cyl && r.os_cyl !== '0.00') {
          osStr += ' / ' + r.os_cyl + ' x ' + r.os_axis + '°';
        }

        const pdStr = r.pd_type === 'single'
          ? r.pd_single
          : 'R' + r.pd_right + ' / L' + r.pd_left;

        const lensProps = {
          '_rx_bundle_id': bundleId,
          '_rx_bundle_role': 'lens',
          'Prescription': this.selections.prescription_type || 'Single Vision',
          'OD (Right Eye)': odStr,
          'OS (Left Eye)': osStr,
          'PD': pdStr,
          '_Frame SKU': this.config.product.sku || ''
        };

        if (rxImageUrl) {
          lensProps['Rx Upload'] = rxImageUrl;
        }

        if (r.rx_notes) {
          lensProps['Rx Notes'] = r.rx_notes;
        }

        // Blue-light blocking — opt-in add-on, clear (optical) lenses only.
        // Record explicit Yes/No so the lab sees the customer's choice on every clear-Rx order.
        if (this.config.flowType === 'optical') {
          lensProps['Blue Light Blocking'] = r.add_blue_light ? 'Yes' : 'No';
        }

        items.push({
          id: lens.shopifyVariantId,
          quantity: 1,
          properties: lensProps
        });

        // Add-ons (future multi_select steps)
        this.steps.forEach(step => {
          if (step.type === 'multi_select' && this.selections[step.id]) {
            this.selections[step.id].forEach(val => {
              const opt = step.options.find(o => o.value === val);
              if (opt && opt.shopifyVariantId) {
                items.push({
                  id: opt.shopifyVariantId,
                  quantity: 1,
                  properties: {
                    '_rx_bundle_id': bundleId,
                    '_rx_bundle_role': 'add_on',
                    '_add_on_type': opt.displayTitle || val
                  }
                });
              }
            });
          }
        });

        // Add to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.description || 'Failed to add to cart');
        }

        // Redirect to cart
        window.location.href = '/cart';

      } catch (err) {
        this.error = err.message;
        this.submitting = false;
      }
    },

    // --- Helpers ---
    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    },

    generateSphOptions(min, max) {
      const options = [];
      for (let v = min; v <= max; v += 0.25) {
        const val = v.toFixed(2);
        const label = v >= 0 ? '+' + val : val;
        options.push({ value: val, label });
      }
      return options;
    },

    generateCylOptions(min, max) {
      const options = [{ value: '', label: 'None' }];
      for (let v = min; v <= max; v += 0.25) {
        if (v === 0) continue;
        const val = v.toFixed(2);
        const label = v >= 0 ? '+' + val : val;
        options.push({ value: val, label });
      }
      return options;
    },

    generateAxisOptions() {
      const options = [{ value: '', label: '—' }];
      for (let i = 1; i <= 180; i++) {
        options.push({ value: i.toString(), label: i.toString() + '°' });
      }
      return options;
    },

    generatePdOptions() {
      const options = [];
      for (let v = 50; v <= 80; v += 0.5) {
        options.push({ value: v.toString(), label: v.toString() });
      }
      return options;
    },

    generateDualPdOptions() {
      const options = [];
      for (let v = 25; v <= 40; v += 0.5) {
        options.push({ value: v.toString(), label: v.toString() });
      }
      return options;
    },

    get sphOptions() {
      return this.generateSphOptions(this.flow.rxLimits.sph.min, this.flow.rxLimits.sph.max);
    },

    get cylOptions() {
      return this.generateCylOptions(this.flow.rxLimits.cyl.min, this.flow.rxLimits.cyl.max);
    },

    get axisOptions() {
      return this.generateAxisOptions();
    },

    get pdOptions() {
      return this.generatePdOptions();
    },

    get dualPdOptions() {
      return this.generateDualPdOptions();
    }

  }));

});
